import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import './Navbar.css';
import logo from '../assests/logo.png';

/* ── Nav links ── */
const NAV_LINKS = [
  { label: 'About Us', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Portfolio', path: '/portfolio' },
  { label: 'Career', path: '/career' },
  { label: 'Training', path: '/training' },
  { label: 'Internship', path: '/internship' },
  {
    label: 'Abroad Studies',
    external: 'https://skillsync.edumilestones.com/',
  },
];

/* ── Search overlay ── */
function SearchOverlay({ onClose }) {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="search-overlay" onClick={onClose}>
      <div
        className="search-overlay__box"
        onClick={(e) => e.stopPropagation()}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="search-overlay__icon"
        >
          <circle
            cx="9"
            cy="9"
            r="6"
            stroke="#7a8899"
            strokeWidth="1.8"
          />
          <path
            d="M13.5 13.5l3 3"
            stroke="#7a8899"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>

        <input
          ref={inputRef}
          type="text"
          placeholder="Search services, resources…"
          className="search-overlay__input"
        />

        <button
          className="search-overlay__close"
          onClick={onClose}
          aria-label="Close search"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path
              d="M4 4l10 10M14 4L4 14"
              stroke="#7a8899"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN NAVBAR
══════════════════════════════════════ */
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  /* ── Open Abroad Studies in NEW TAB ── */
  const handleAbroadStudies = (e) => {
    e.preventDefault();
    e.stopPropagation();

    window.open(
      'https://skillsync.edumilestones.com/',
      '_blank',
      'noopener,noreferrer'
    );

    setMobileOpen(false);
  };

  return (
    <>
      <header
        className={`navbar ${
          scrolled ? 'navbar--scrolled' : ''
        }`}
      >
        <div className="navbar__inner">

          {/* ── LOGO → HOME ── */}
          <a
            href="/"
            className="navbar__logo"
            onClick={(e) => {
              e.preventDefault();
              setMobileOpen(false);
              navigate('/');
            }}
          >
            <img
              src={logo}
              alt="SkillSync Pro"
              className="navbar__logo-img"
            />
          </a>

          {/* ── DESKTOP NAV ── */}
          <nav
            className="navbar__nav"
            aria-label="Main navigation"
          >
            <ul className="navbar__list">

              {NAV_LINKS.map(({ label, path, external }) => (
                <li
                  key={label}
                  className="nav-item"
                >
                  {external ? (
                    <a
                      href={external}
                      className="nav-link"
                      onClick={handleAbroadStudies}
                    >
                      {label}
                    </a>
                  ) : (
                    <NavLink
                      to={path}
                      className={({ isActive }) =>
                        `nav-link ${
                          isActive
                            ? 'nav-link--active'
                            : ''
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  )}
                </li>
              ))}

            </ul>
          </nav>

          {/* ── RIGHT ACTIONS ── */}
          <div className="navbar__actions">

            {/* Search */}
            {/*
            <button
              className="nav-icon-btn"
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
            >
              <svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
              >
                <circle
                  cx="8.5"
                  cy="8.5"
                  r="5.5"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M13 13l3.5 3.5"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            */}

            {/* Divider */}
            {/*
            <div className="nav-divider" />
            */}

            {/* Sign In */}
            {/*
            <NavLink
              to="/login"
              className={({ isActive }) =>
                `nav-signin ${
                  isActive
                    ? 'nav-signin--active'
                    : ''
                }`
              }
            >
              Sign In
            </NavLink>
            */}

            {/* Contact */}
            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `nav-signup ${
                  isActive
                    ? 'nav-signup--active'
                    : ''
                }`
              }
            >
              Contact
            </NavLink>

            {/* Hamburger */}
            <button
              className={`navbar__burger ${
                mobileOpen
                  ? 'navbar__burger--open'
                  : ''
              }`}
              onClick={() =>
                setMobileOpen((p) => !p)
              }
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              <span />
              <span />
              <span />
            </button>

          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════
          MOBILE MENU
      ══════════════════════════════════════ */}
      <div
        className={`navbar__mobile ${
          mobileOpen
            ? 'navbar__mobile--open'
            : ''
        }`}
        onClick={() => setMobileOpen(false)}
      >
        <div
          className="navbar__mobile-card"
          onClick={(e) => e.stopPropagation()}
        >

          <nav className="navbar__mobile-nav">

            {/* Home */}
            <NavLink
              to="/"
              className="mobile-link"
              onClick={() =>
                setMobileOpen(false)
              }
            >
              Home
            </NavLink>

            {/* Navigation Links */}
            {NAV_LINKS.map(({ label, path, external }) => (
              external ? (
                <a
                  key={label}
                  href={external}
                  className="mobile-link"
                  onClick={handleAbroadStudies}
                >
                  {label}
                </a>
              ) : (
                <NavLink
                  key={label}
                  to={path}
                  className={({ isActive }) =>
                    `mobile-link ${
                      isActive
                        ? 'mobile-link--active'
                        : ''
                    }`
                  }
                  onClick={() =>
                    setMobileOpen(false)
                  }
                >
                  {label}
                </NavLink>
              )
            ))}

          </nav>

          {/* ── MOBILE ACTIONS ── */}
          <div className="navbar__mobile-actions">

            <NavLink
              to="/login"
              className="nav-signin"
              onClick={() =>
                setMobileOpen(false)
              }
            >
              Sign In
            </NavLink>

            <NavLink
              to="/contact"
              className="nav-signup"
              onClick={() =>
                setMobileOpen(false)
              }
            >
              Contact
            </NavLink>

          </div>

        </div>
      </div>

      {/* ── Search Overlay ── */}
      {searchOpen && (
        <SearchOverlay
          onClose={() =>
            setSearchOpen(false)
          }
        />
      )}
    </>
  );
}